import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Layout constants (keep similar to CalendarView)
const SLOT_HEIGHT = 36; // px

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const pad2 = (n) => String(n).padStart(2, "0");

const minutesFromMidnight = (d) => d.getHours() * 60 + d.getMinutes();

function buildDayMeta(workingHours, date) {
  const fallback = { isClosed: false, openMinutes: 8 * 60, closeMinutes: 20 * 60 };
  if (!workingHours || !date) return fallback;

  const dayIndex = (date.getDay() + 6) % 7; // luni=0

  const dayCfg = Array.isArray(workingHours)
    ? workingHours[dayIndex]
    : workingHours?.days?.[dayIndex] ?? workingHours?.[dayIndex] ?? null;

  if (!dayCfg) return fallback;

  const isClosed = Boolean(dayCfg?.is_closed ?? dayCfg?.closed);
  if (isClosed) return { isClosed: true, openMinutes: 0, closeMinutes: 0 };

  const open = dayCfg?.open ?? dayCfg?.start ?? dayCfg?.open_time ?? "08:00";
  const close = dayCfg?.close ?? dayCfg?.end ?? dayCfg?.close_time ?? "20:00";

  const parseHM = (s) => {
    const [hh, mm] = String(s || "").split(":");
    const h = Number(hh);
    const m = Number(mm);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const openMinutes = parseHM(open) ?? fallback.openMinutes;
  const closeMinutes = parseHM(close) ?? fallback.closeMinutes;

  if (closeMinutes <= openMinutes) return fallback;
  return { isClosed: false, openMinutes, closeMinutes };
}

function buildTimeSlots({ openMinutes, closeMinutes, slotMinutes }) {
  const slots = [];
  const total = Math.max(slotMinutes, closeMinutes - openMinutes);
  const totalSlots = Math.ceil(total / slotMinutes);

  for (let i = 0; i < totalSlots; i++) {
    const m = openMinutes + i * slotMinutes;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    slots.push({
      minutes: m,
      label: `${pad2(h)}:${pad2(mm)}`,
      isHour: mm === 0,
      isHalf: mm === 30,
    });
  }
  return { slots, totalSlots };
}

function layoutLanes(evts) {
  const items = [...evts].sort((a, b) => a.startRow - b.startRow);
  const laneEnds = [];
  const out = [];

  for (const it of items) {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > it.startRow) lane++;
    if (lane === laneEnds.length) laneEnds.push(it.endRow);
    else laneEnds[lane] = it.endRow;
    out.push({ ...it, lane });
  }

  const lanesCount = Math.max(1, laneEnds.length || 1);
  return { items: out, lanesCount };
}

const lineClassForBoundary = (slotStart) => {
  // slotStart = timeSlots[idx] (începutul slotului). Linia e exact la boundary.
  if (slotStart?.isHour) return "border-border/60 border-solid";
  if (slotStart?.isHalf) return "border-border/40 border-dotted";
  return "border-border/25 border-dotted";
};

export default function MobileResourceGrid({
  date,
  appointments,
  staff,
  staffIds,
  slotMinutes = 15,
  workingHours,
  onDateSelect,
  onAppointmentClick,
}) {
  const { t } = useTranslation();
  const dayMeta = useMemo(() => buildDayMeta(workingHours, date), [workingHours, date]);

  const { slots: timeSlots, totalSlots } = useMemo(
    () => buildTimeSlots({ ...dayMeta, slotMinutes }),
    [dayMeta, slotMinutes]
  );

  const staffList = useMemo(() => {
    const map = new Map((staff || []).map((s) => [String(s.id), s]));
    return (staffIds || []).map((id) => ({
      id: String(id),
      label: map.get(String(id))?.label || `#${id}`,
    }));
  }, [staff, staffIds]);

  const dayEvents = useMemo(() => {
    const all = Array.isArray(appointments) ? appointments : [];
    return all.filter((a) => {
      const s = a?.start instanceof Date ? a.start : new Date(a?.start);
      if (!s || Number.isNaN(s.getTime())) return false;
      return sameDay(s, date);
    });
  }, [appointments, date]);

  const showMinorLabels = slotMinutes >= 10 && slotMinutes <= 30;

  if (dayMeta.isClosed) {
    return (
      <div className="border-t border-border/60 py-4 px-2 text-sm text-muted-foreground">
        Locația este închisă în această zi.
      </div>
    );
  }

  if (!staffList.length) {
    return (
      <div className="border-t border-border/60 py-4 px-2 text-sm text-muted-foreground">
        Nu există personal disponibil.
      </div>
    );
  }

  const totalHeight = totalSlots * SLOT_HEIGHT;
  const shouldScroll = staffList.length > 6;
  const colWidth = shouldScroll ? 160 : undefined;

  return (
    <div className="border-t border-border/60 bg-background">
      {/* Header */}
      <div className="flex bg-muted/60 text-xs text-foreground/70 border-b border-border/60">
        <div className="w-11 shrink-0 px-1.5 py-2">{t("calendar.colTime")}</div>

        <div className={shouldScroll ? "flex-1 overflow-x-auto" : "flex-1"}>
          <div
            className={shouldScroll ? "flex" : "grid"}
            style={
              shouldScroll
                ? { minWidth: staffList.length * (colWidth || 160) }
                : { gridTemplateColumns: `repeat(${staffList.length}, minmax(0, 1fr))` }
            }
          >
            {staffList.map((s) => (
              <div
                key={s.id}
                className="px-1.5 py-2 text-center font-medium truncate border-l border-border/60"
                style={shouldScroll ? { width: colWidth } : undefined}
                title={s.label}
              >
                {s.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative flex">
        {/* ✅ ONE SINGLE GRID OVERLAY (aliniază perfect dotted-ul peste toate coloanele) */}
        <div className="absolute inset-0 pointer-events-none">
          {/* linia de sus */}
          <div className="absolute left-0 right-0 border-t border-border/60" style={{ top: 0 }} />

          {/* liniile interne: la fiecare boundary (idx * SLOT_HEIGHT) */}
          {timeSlots.map((t, idx) => {
            if (idx === 0) return null; // top line already drawn
            return (
              <div
                key={`line_${idx}`}
                className={`absolute left-0 right-0 border-t ${lineClassForBoundary(t)}`}
                style={{ top: idx * SLOT_HEIGHT }}
              />
            );
          })}

          {/* linia de jos */}
          <div
            className="absolute left-0 right-0 border-t border-border/60"
            style={{ top: totalHeight }}
          />
        </div>

        {/* Time column */}
        <div className="w-11 shrink-0">
          <div style={{ height: totalHeight }}>
            {timeSlots.map((t, idx) => {
              const mm = t.minutes % 60;
              const label = t.isHour ? t.label : showMinorLabels ? pad2(mm) : "";

              return (
                <div
                  key={`${t.label}_${idx}`}
                  className={[
                    "px-1 pr-1 text-[10px] leading-none",
                    "flex items-center justify-end", // ✅ centrat vertical
                    t.isHour ? "text-foreground/80" : "text-muted-foreground",
                  ].join(" ")}
                  style={{ height: SLOT_HEIGHT }}
                >
                  {label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Staff columns */}
        <div className={shouldScroll ? "flex-1 overflow-x-auto" : "flex-1"}>
          <div
            className={shouldScroll ? "flex" : "grid"}
            style={
              shouldScroll
                ? { minWidth: staffList.length * (colWidth || 160) }
                : { gridTemplateColumns: `repeat(${staffList.length}, minmax(0, 1fr))` }
            }
          >
            {staffList.map((s) => {
              const colEventsRaw = dayEvents
                .filter((a) => String(a?.staffId || a?._raw?.staff_id || "") === String(s.id))
                .map((a) => {
                  const start = a.start instanceof Date ? a.start : new Date(a.start);
                  const end = a.end instanceof Date ? a.end : new Date(a.end);
                  const startMin = minutesFromMidnight(start);
                  const endMin = minutesFromMidnight(end);
                  const clampedStart = Math.max(dayMeta.openMinutes, Math.min(startMin, dayMeta.closeMinutes));
                  const clampedEnd = Math.max(clampedStart + slotMinutes, Math.min(endMin, dayMeta.closeMinutes));

                  const startRow = Math.floor((clampedStart - dayMeta.openMinutes) / slotMinutes) + 1;
                  const endRow = Math.max(
                    startRow + 1,
                    Math.ceil((clampedEnd - dayMeta.openMinutes) / slotMinutes) + 1
                  );

                  return { ...a, startRow, endRow };
                });

              const { items: colEvents, lanesCount } = layoutLanes(colEventsRaw);

              return (
                <div
                  key={s.id}
                  className="relative border-l border-border/60"
                  style={shouldScroll ? { width: colWidth } : undefined}
                >
                  
                  {/* background grid + click-to-create (fără borders, liniile vin din overlay) */}
                  <div
                    className="grid"
                    style={{
                      height: totalHeight,
                      gridTemplateRows: `repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
                    }}
                  >
                    {timeSlots.map((t, idx) => (
                      <button
                        key={`${s.id}_${t.label}_${idx}`}
                        type="button"
                        className="block w-full h-full p-0 m-0 border-0 rounded-none bg-transparent appearance-none text-left focus:outline-none focus:ring-0 active:bg-muted/40"
                        style={{ gridRow: `${idx + 1} / ${idx + 2}` }}
                        onClick={() => onDateSelect?.(date, t.label, s.id)}
                        title={`Adaugă la ${t.label}`}
                      />
                    ))}
                  </div>


                  {/* events overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    {colEvents.map((a) => {
                      const top = (a.startRow - 1) * SLOT_HEIGHT;
                      const height = Math.max(SLOT_HEIGHT, (a.endRow - a.startRow) * SLOT_HEIGHT);
                      const w = 100 / lanesCount;
                      const left = a.lane * w;

                      return (
                        <button
                          key={a.id}
                          type="button"
                          className="absolute pointer-events-auto rounded-md border border-border/60 bg-card px-1.5 py-1 text-left overflow-hidden"
                          style={{ top, height, left: `${left}%`, width: `${w}%` }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onAppointmentClick?.(a);
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[11px] font-semibold truncate">
                              {a.clientName || a.client || "Client"}
                            </div>
                            <span className="inline-flex items-center">
                              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                            </span>
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {a.serviceName || a.service || ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
